import { useState, useEffect } from 'react';
import { io } from 'socket.io-client';

// WebSocket message types using discriminated unions
const MESSAGE_TYPES = {
    INIT_HANDSHAKE: 'init-handshake',
    ADMIN_HANDSHAKE: 'admin-handshake',
    HANDSHAKE_COMPLETED: 'handshake-completed',
    GET_USERS: 'get-users',
    ONLINE_USER_COUNT: 'online-user-count',
    USER_STATUS_CHANGED: 'user-status-changed',
    USER_LOGOUT: 'user-logout',
    PLAYER_ADDED_TO_QUEUE: 'player-added-to-queue',
    PLAYER_REMOVED_FROM_QUEUE: 'player-removed-from-queue',
    LOBBY_CREATED: 'lobby-created',
    LOBBY_DELETED: 'lobby-deleted',
    MATCH_CREATED: 'match-created',
    MATCH_STARTED: 'match-started',
    MATCH_FINISHED: 'match-finished',
    MATCH_DELETED: 'match-deleted',
    SERVERS_ALIVE_COUNT: 'servers-alive-count',
    DASHBOARD_OVERVIEW_UPDATE: 'dashboard-overview-update',
    AGENT_ANNOUNCEMENT: 'agent-announcement',
    AGENT_JOURNAL_BATCH: 'agent-journal-batch',
    CAPACITY_PLANNER_NARRATIVE: 'capacity-planner-narrative',
};

// Connection states using discriminated unions
const CONNECTION_STATES = {
    DISCONNECTED: 'disconnected',
    CONNECTING: 'connecting',
    CONNECTED: 'connected',
    RECONNECTING: 'reconnecting',
    ERROR: 'error'
};

// Singleton WebSocket manager for admin
class AdminWebSocketManager {
    constructor() {
        this.socket = null;
        this.user = null;
        this.listeners = new Set();
        this.connectionState = CONNECTION_STATES.DISCONNECTED;
        
        // Redux dispatch (set from App)
        this.dispatch = null;

        // Bind methods
        this.connect = this.connect.bind(this);
        this.disconnect = this.disconnect.bind(this);
        this.send = this.send.bind(this);
        this.subscribe = this.subscribe.bind(this);
        this.unsubscribe = this.unsubscribe.bind(this);
        this.notifyListeners = this.notifyListeners.bind(this);
        this.setDispatch = this.setDispatch.bind(this);
        this.subscribeMatchDebug = this.subscribeMatchDebug.bind(this);
        this.unsubscribeMatchDebug = this.unsubscribeMatchDebug.bind(this);
    }

    setDispatch(dispatch) {
        this.dispatch = dispatch;
    }

    connect(user) {
        if (this.socket?.connected) {
            console.log('[Socket.IO Admin] Already connected');
            return;
        }

        this.user = user;
        this.connectionState = CONNECTION_STATES.CONNECTING;
        this.notifyListeners();

        // Determine connection URL based on environment
        let socketUrl;
        let socketPath;
        
        if (process.env.NODE_ENV === 'production') {
            // Production: Connect through nginx reverse proxy at /edge/
            // Namespace is /dashboard, but the Socket.IO HTTP endpoint is exposed via /edge/socket.io/ in nginx.
            socketUrl = '/dashboard';
            socketPath = '/edge/socket.io/';
        } else {
            // Development: Connect directly to local edge server
            socketUrl = 'http://localhost:3010/dashboard';
            socketPath = '/socket.io/';
        }

        console.log(`[Socket.IO Admin] Connecting to: ${socketUrl} with path: ${socketPath}`);

        try {
            // Connect to /dashboard namespace (renamed to avoid conflict with Admin UI)
            this.socket = io(socketUrl, {
                path: socketPath,
                transports: ['websocket', 'polling'],
                reconnection: true,
                reconnectionDelay: 1000,
                reconnectionDelayMax: 5000,
                reconnectionAttempts: 5,
                timeout: 20000,
                autoConnect: true,
                withCredentials: true,
                secure: process.env.NODE_ENV === 'production'
            });

            this.setupEventListeners();
        } catch (error) {
            console.error('[Socket.IO Admin] Failed to create connection:', error);
            this.connectionState = CONNECTION_STATES.ERROR;
            this.notifyListeners();
        }
    }

    setupEventListeners() {
        // Connection events
        this.socket.on('connect', () => {
            console.log(`[Socket.IO Admin] ✅ Connected with ID: ${this.socket.id}`);
            this.connectionState = CONNECTION_STATES.CONNECTED;
            this.notifyListeners();
        });

        this.socket.on('disconnect', (reason) => {
            console.log(`[Socket.IO Admin] Disconnected: ${reason}`);
            this.connectionState = CONNECTION_STATES.DISCONNECTED;
            this.notifyListeners();
        });

        this.socket.on('connect_error', (error) => {
            console.error('[Socket.IO Admin] Connection error:', error);
            this.connectionState = CONNECTION_STATES.ERROR;
            this.notifyListeners();
        });

        this.socket.io.on('reconnect_attempt', () => {
            console.log('[Socket.IO Admin] Attempting to reconnect...');
            this.connectionState = CONNECTION_STATES.RECONNECTING;
            this.notifyListeners();
        });

        this.socket.io.on('reconnect', () => {
            console.log('[Socket.IO Admin] ✅ Reconnected successfully');
            this.connectionState = CONNECTION_STATES.CONNECTED;
            this.notifyListeners();
        });

        // Handle init-handshake
        this.socket.on('init-handshake', (message) => {
            console.log(`[Socket.IO Admin] Received init-handshake, clientId: ${message.clientId}`);
            
            // Send admin handshake
            this.socket.emit('admin-handshake', {
                messageId: MESSAGE_TYPES.ADMIN_HANDSHAKE,
                clientId: message.clientId,
            });
        });

        // Handle handshake-completed
        this.socket.on('handshake-completed', (message) => {
            console.log('[Socket.IO Admin] ✅ Admin handshake completed!');
            
            // Request user count
            this.socket.emit('get-users', {
                messageId: MESSAGE_TYPES.GET_USERS,
            });

            this.notifyListeners({ ...message, messageId: 'handshake-completed' });
        });

        // Handle monitoring data with Redux dispatch
        this.socket.on('online-user-count', (message) => {
            if (this.dispatch) {
                this.dispatch({ 
                    type: 'SET_QUEUE_ANALITYCS_SUMMARY', 
                    queueAnalitycsSummary: {
                        onlineUserCount: message.count || 0,
                    },
                });
            }
            this.notifyListeners({ ...message, messageId: 'online-user-count' });
        });

        this.socket.on('user-status-changed', (message) => {
            if (this.dispatch) {
                this.dispatch({ 
                    type: 'SET_QUEUE_ANALITYCS_SUMMARY', 
                    queueAnalitycsSummary: {
                        onlineUserCount: message.onlineUserCount || 0,
                    },
                });
            }
            this.notifyListeners({ ...message, messageId: 'user-status-changed' });
        });

        this.socket.on('player-added-to-queue', (message) => {
            if (this.dispatch) {
                this.dispatch({ 
                    type: 'SET_QUEUE_ANALITYCS_SUMMARY', 
                    queueAnalitycsSummary: {
                        totalPlayersInQueue: message.totalPlayersInQueue || 0,
                        averageQueueDuration: message.averageQueueDuration || 0,
                    },
                });
            }
            this.notifyListeners({ ...message, messageId: 'player-added-to-queue' });
        });

        this.socket.on('player-removed-from-queue', (message) => {
            if (this.dispatch) {
                this.dispatch({ 
                    type: 'SET_QUEUE_ANALITYCS_SUMMARY', 
                    queueAnalitycsSummary: {
                        totalPlayersInQueue: message.totalPlayersInQueue || 0,
                        averageQueueDuration: message.averageQueueDuration || 0,
                    },
                });
            }
            this.notifyListeners({ ...message, messageId: 'player-removed-from-queue' });
        });

        this.socket.on('lobby-created', (message) => {
            if (this.dispatch) {
                this.dispatch({ 
                    type: 'SET_QUEUE_ANALITYCS_SUMMARY', 
                    queueAnalitycsSummary: {
                        totalLobbies: message.totalLobbies || 0,
                    },
                });
            }
            this.notifyListeners({ ...message, messageId: 'lobby-created' });
        });

        this.socket.on('lobby-deleted', (message) => {
            if (this.dispatch) {
                this.dispatch({ 
                    type: 'SET_QUEUE_ANALITYCS_SUMMARY', 
                    queueAnalitycsSummary: {
                        totalLobbies: message.totalLobbies || 0,
                    },
                });
            }
            this.notifyListeners({ ...message, messageId: 'lobby-deleted' });
        });

        this.socket.on('match-created', (message) => {
            if (this.dispatch) {
                this.dispatch({ 
                    type: 'SET_MM_ACTIVITY_SUMMARY', 
                    mmActivitySummary: {
                        matchesLive: message.matchesLive || 0,
                        matchesCreatedToday: message.matchesCreatedToday || 0,
                    }
                });
            }
            this.notifyListeners({ ...message, messageId: 'match-created' });
        });

        this.socket.on('match-deleted', (message) => {
            if (this.dispatch) {
                this.dispatch({ 
                    type: 'SET_MM_ACTIVITY_SUMMARY', 
                    mmActivitySummary: {
                        matchesLive: message.matchesLive || 0,
                        matchesCreatedToday: message.matchesCreatedToday || 0,
                    }
                });
            }
            this.notifyListeners({ ...message, messageId: 'match-deleted' });
        });

        this.socket.on('match-started', (message) => {
            if (this.dispatch) {
                this.dispatch({ 
                    type: 'SET_MM_ACTIVITY_SUMMARY', 
                    mmActivitySummary: {
                        matchesLive: message.matchesLive || 0,
                        matchesCreatedToday: message.matchesCreatedToday || 0,
                    }
                });
            }
            this.notifyListeners({ ...message, messageId: 'match-started' });
        });

        this.socket.on('match-finished', (message) => {
            if (this.dispatch) {
                this.dispatch({ 
                    type: 'SET_MM_ACTIVITY_SUMMARY', 
                    mmActivitySummary: {
                        matchesLive: message.matchesLive || 0,
                        matchesCreatedToday: message.matchesCreatedToday || 0,
                        matchesCompletedToday: message.matchesCompletedToday || 0,
                        completionRate: message.completionRate || 0,
                    }
                });
            }
            this.notifyListeners({ ...message, messageId: 'match-finished' });
        });

        // Servers alive count (used by MonitoringSection)
        this.socket.on('servers-alive-count', (message) => {
            if (this.dispatch) {
                this.dispatch({
                    type: 'SET_SERVERS_ALIVE_COUNT',
                    serversAliveCount: message.serversAliveCount || 0,
                });
            }
            this.notifyListeners({ ...message, messageId: 'servers-alive-count' });
        });

        // Dashboard updates (new monitoring dashboard)
        this.socket.on('dashboard-overview-update', (message) => {
            this.notifyListeners({ ...message, messageId: 'dashboard-overview-update' });
        });

        this.socket.on('agent-announcement', (message) => {
            this.notifyListeners({ ...message, messageId: 'agent-announcement' });
        });

        this.socket.on('agent-journal-batch', (message) => {
            this.notifyListeners({ ...message, messageId: 'agent-journal-batch' });
        });

        this.socket.on('capacity-planner-narrative', (message) => {
            this.notifyListeners({ ...message, messageId: 'capacity-planner-narrative' });
        });

        // Anticheat live debugger events
        ["match-debug-frame", "match-debug-event", "match-debug-status", "match-debug-subscribed"].forEach((eventName) => {
            this.socket.on(eventName, (message) => {
                this.notifyListeners({ ...message, messageId: eventName });
            });
        });

        // Handle any other events generically
        this.socket.onAny((eventName, ...args) => {
            // Only log if it's not one of our known message types
            if (!Object.values(MESSAGE_TYPES).includes(eventName)) {
                console.log(`[Socket.IO Admin] Unknown event: ${eventName}`, args);
            }
        });
    }

    send(message) {
        if (this.socket?.connected) {
            const { messageId, ...data } = message;
            this.socket.emit(messageId, message);
            console.log(`[Socket.IO Admin] Sent message: ${messageId}`, data);
        } else {
            console.warn('[Socket.IO Admin] Not connected, message not sent:', message);
        }
    }

    disconnect() {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
        }
        this.connectionState = CONNECTION_STATES.DISCONNECTED;
        this.notifyListeners();
    }

    subscribeMatchDebug(matchId) {
        if (this.socket?.connected && matchId) {
            this.socket.emit("subscribe-match-debug", { matchId });
        }
    }

    unsubscribeMatchDebug(matchId) {
        if (this.socket?.connected && matchId) {
            this.socket.emit("unsubscribe-match-debug", { matchId });
        }
    }

    subscribe(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    unsubscribe(listener) {
        this.listeners.delete(listener);
    }

    notifyListeners(message = null) {
        this.listeners.forEach(listener => {
            try {
                listener({
                    connectionState: this.connectionState,
                    clientId: this.socket?.id,
                    message: message
                });
            } catch (error) {
                console.error('[Socket.IO Admin] Error in listener:', error);
            }
        });
    }

    getConnectionState() {
        return this.connectionState;
    }

    getClientId() {
        return this.socket?.id;
    }

    isConnected() {
        return this.socket?.connected || false;
    }
}

const adminWsManager = new AdminWebSocketManager();

export function useAdminWebSocketStore() {
    const [state, setState] = useState(() => ({
        connectionState: adminWsManager.getConnectionState(),
        clientId: adminWsManager.getClientId(),
        isConnected: adminWsManager.isConnected(),
    }));

    useEffect(() => {
        const unsubscribe = adminWsManager.subscribe(() => {
            setState({
                connectionState: adminWsManager.getConnectionState(),
                clientId: adminWsManager.getClientId(),
                isConnected: adminWsManager.isConnected(),
            });
        });

        return unsubscribe;
    }, []);

    return {
        ...state,
        connect: adminWsManager.connect,
        disconnect: adminWsManager.disconnect,
        send: adminWsManager.send,
        subscribe: adminWsManager.subscribe,
        setDispatch: adminWsManager.setDispatch,
        subscribeMatchDebug: adminWsManager.subscribeMatchDebug,
        unsubscribeMatchDebug: adminWsManager.unsubscribeMatchDebug
    };
}

// Export the manager for direct access when needed
export { adminWsManager, MESSAGE_TYPES, CONNECTION_STATES };
